// Generic preview abstraction for the global Layer Properties console.
//
// Picks the most useful preview for a layer purely from its kind + endpoints +
// style, with no rendering and no I/O. The Preview tab can optionally enrich
// the descriptor asynchronously (fetching PMTiles metadata, tileset.json, or a
// terrain tilejson) — but the base descriptor always works offline and is the
// unit-tested contract.

import type { Layer } from '@geosoft/viewer-sdk';
import { asRecord, styleRecord } from './source';
import type { LayerPreview, LayerSourceDiagnostics } from './types';

function numberPair(value: unknown): [number, number] | null {
  if (Array.isArray(value) && value.length >= 2) {
    const [a, b] = value;
    if (typeof a === 'number' && typeof b === 'number') return [a, b];
  }
  const rec = asRecord(value);
  if (rec && typeof rec['min'] === 'number' && typeof rec['max'] === 'number') {
    return [rec['min'] as number, rec['max'] as number];
  }
  return null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function bbox4(layer: Layer): readonly [number, number, number, number] | null {
  const b = layer.bbox;
  if (!Array.isArray(b) || b.length < 4) return null;
  return [b[0], b[1], b[2], b[3]];
}

export function buildLayerPreview(
  layer: Layer,
  source: LayerSourceDiagnostics,
): LayerPreview {
  const style = styleRecord(layer) ?? {};

  // Geographic footprint (lon/lat) shared across kinds so the Preview tab can
  // draw a real map for PMTiles / tilesets / terrain, not just `extent`.
  const footprint = bbox4(layer);
  const footprintCenter: readonly [number, number] | null = footprint
    ? [(footprint[0] + footprint[2]) / 2, (footprint[1] + footprint[3]) / 2]
    : null;

  // PMTiles vector / raster archive.
  if (layer.endpoints.pmtiles) {
    return {
      kind: 'pmtiles',
      archiveUrl: layer.endpoints.pmtiles,
      selectedSourceLayer: source.selectedPmtilesSourceLayer,
      bbox: footprint,
      center: footprintCenter,
    };
  }

  // 3D Tiles tileset (3dtiles + tileset-backed point clouds).
  if (layer.endpoints.tileset3d) {
    return {
      kind: 'tileset',
      tilesetUrl: layer.endpoints.tileset3d,
      bbox: footprint,
      center: footprintCenter,
    };
  }

  // BIM / GLB / glTF model (direct model endpoint).
  if (layer.endpoints.model && (layer.kind === 'bim' || layer.kind === '3dtiles')) {
    return {
      kind: 'model',
      modelUrl: layer.endpoints.model,
      bbox: footprint,
      center: footprintCenter,
    };
  }

  // Terrain DEM.
  if (layer.kind === 'terrain' && layer.endpoints.terrain) {
    return {
      kind: 'terrain',
      endpoint: layer.endpoints.terrain,
      nodata: str(style['nodataValue']) ?? str(style['titilerNodata']),
      colormap: str(style['colormapName']) ?? str(style['colorRamp']),
      minmax: numberPair(style['minmax']) ?? numberPair(style['colorRescaleRange']),
      bbox: footprint,
      center: footprintCenter,
    };
  }

  // Environment layers have no spatial source — never show a stray extent.
  if (layer.kind === 'environment' || layer.id.startsWith('layer:environment:')) {
    return { kind: 'none', reason: 'Environment layers have no spatial source to preview.' };
  }

  // Generic extent preview when a real bbox is present.
  const bbox = bbox4(layer);
  if (bbox) {
    return {
      kind: 'extent',
      bbox,
      center: [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2],
      sourceCrs: typeof layer.sourceCrs === 'string' ? layer.sourceCrs : layer.crs,
    };
  }

  return { kind: 'none', reason: 'No previewable source (bbox/endpoint) is available for this layer.' };
}
